// crewbit-admin/src/hooks/useActiveBusiness.ts
//
// Hook contract: docs/references/MONEY_MODULE_WIRING_v1.md §3.10.
// Maps to DATA spec §5.10 set_active_business RPC.
//
// Mirrors crewbit-mobile/hooks/useActiveBusiness.ts. The two implementations
// are kept in sync by hand — the three-runtime duplication (mobile RN, admin
// TSX, web JSX) is intentional during launch (see WIRING §1.1).
//
// Behaviour:
// - hydrate() resolves memberships and either auto-picks (single membership),
//   surfaces requiresSelection=true (multi-membership, no active session row),
//   or stays idle (zero memberships).
// - setActiveBusiness(id) calls set_active_business RPC, updates state, and
//   broadcasts a 'crewbit:active-business-changed' window event so list hooks
//   can refetch.
// - reset() clears state on signOut.

import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { getOrCreateSessionToken } from '../lib/sessionToken';
import { callMoneyRpc, type MoneyRpcResult } from '../lib/moneyRpc';
import { useAuthStore } from '../stores/authStore';

export type Membership = { id: string; name: string };

/** One business membership as resolved by get_active_actor_context(). */
export interface ActorBusinessRole {
  business_id: string;
  business_name: string;
  role: string;
}

/** Server-resolved actor context (Client-App Phase 1, Spec §1 / §E). */
export interface ActorContext {
  public_user_id: string | null;
  business_id: string | null;
  current_mode: 'client' | 'business';
  business_roles: ActorBusinessRole[];
}

interface ActiveBusinessState {
  activeBusinessId: string | null;
  activeBusinessName: string | null;
  memberships: Membership[];
  requiresSelection: boolean;
  /** Server-resolved current_mode (null until hydrated / when unresolvable). */
  currentMode: 'client' | 'business' | null;
  hydrated: boolean;
  hydrating: boolean;

  hydrate: () => Promise<void>;
  setActiveBusiness: (
    businessId: string,
  ) => Promise<MoneyRpcResult<{ businessId: string; businessName: string }>>;
  reset: () => void;
}

export const ACTIVE_BUSINESS_CHANGED_EVENT = 'crewbit:active-business-changed';

export const useActiveBusinessStore = create<ActiveBusinessState>((set, get) => ({
  activeBusinessId: null,
  activeBusinessName: null,
  memberships: [],
  requiresSelection: false,
  currentMode: null,
  hydrated: false,
  hydrating: false,

  hydrate: async () => {
    if (get().hydrating) return;
    const session = useAuthStore.getState().session;
    const authId = session?.user?.id ?? null;
    if (!authId) {
      set({
        activeBusinessId: null,
        activeBusinessName: null,
        memberships: [],
        requiresSelection: false,
        currentMode: null,
        hydrated: true,
        hydrating: false,
      });
      return;
    }

    set({ hydrating: true });

    // Server-truth context first (Client-App Phase 1, mirrors mobile): if this
    // session token is in client mode, hydrate must NOT auto-pick a business —
    // set_active_business stamps current_mode='business' and would silently
    // yank the session out of client mode. (Admin has no client surface, but a
    // dual-surface session token must not be stomped.)
    const { data: ctxEnvelope } = await supabase.rpc('get_active_actor_context');
    const serverContext: ActorContext | null =
      ctxEnvelope?.success === true ? (ctxEnvelope.context as ActorContext) : null;

    const { data: rows, error: membersErr } = await supabase
      .from('users')
      .select('business_id, businesses:business_id(name)')
      .eq('auth_id', authId)
      .not('business_id', 'is', null);

    if (membersErr) {
      set({ hydrating: false, hydrated: true });
      return;
    }

    // Supabase's TS codegen types FK-resolved fields as an array even when the
    // FK is to-one. The runtime shape is the resolved row object (or null);
    // handle both defensively to keep the inferred + actual shapes happy.
    const memberships: Membership[] = (rows ?? [])
      .map((r: { business_id: string | null; businesses: { name: string }[] | { name: string } | null }) => {
        const biz = Array.isArray(r.businesses) ? r.businesses[0] : r.businesses;
        return {
          id: r.business_id as string,
          name: biz?.name ?? 'Unnamed business',
        };
      })
      .filter((m, i, arr) => arr.findIndex((x) => x.id === m.id) === i);

    // Client mode (server truth): keep the session in client context — no
    // business auto-pick, no picker.
    if (serverContext?.current_mode === 'client') {
      set({
        activeBusinessId: null,
        activeBusinessName: null,
        memberships,
        requiresSelection: false,
        currentMode: 'client',
        hydrating: false,
        hydrated: true,
      });
      return;
    }

    // Single-membership: auto-pick. We don't probe the resolver first
    // because that helper has its own single-membership fallback — a non-null
    // return doesn't prove a user_session_context row exists, so relying on it
    // would skip the upsert and leave the row absent. set_active_business is
    // an idempotent UPSERT; calling it on every hydrate refreshes expires_at.
    // WIRING §3.10 + §9 (Phase B.2.5).
    if (memberships.length === 1) {
      const sessionToken = getOrCreateSessionToken();
      const result = await callMoneyRpc<{ business_id: string; business_name: string; expires_at: string }>(
        'set_active_business',
        { p_business_id: memberships[0].id, p_session_token: sessionToken },
      );
      if (result.success) {
        set({
          activeBusinessId: result.data.business_id,
          activeBusinessName: result.data.business_name,
          memberships,
          requiresSelection: false,
          currentMode: 'business',
          hydrating: false,
          hydrated: true,
        });
        window.dispatchEvent(new CustomEvent(ACTIVE_BUSINESS_CHANGED_EVENT));
        return;
      }
      set({
        activeBusinessId: null,
        activeBusinessName: null,
        memberships,
        requiresSelection: false,
        currentMode: serverContext?.current_mode ?? null,
        hydrating: false,
        hydrated: true,
      });
      return;
    }

    // Multi-membership: the context read above already resolved it — a
    // business_id iff a user_session_context row exists for
    // (auth.uid(), session_token); NO_ACTIVE_CONTEXT (null here) otherwise
    // (the resolver's single-membership fallback doesn't apply here).
    if (memberships.length > 1) {
      const activeBizId = serverContext?.business_id ?? null;
      if (activeBizId) {
        const match = memberships.find((m) => m.id === activeBizId);
        set({
          activeBusinessId: activeBizId,
          activeBusinessName: match?.name ?? null,
          memberships,
          requiresSelection: false,
          currentMode: 'business',
          hydrating: false,
          hydrated: true,
        });
        return;
      }
      set({
        activeBusinessId: null,
        activeBusinessName: null,
        memberships,
        requiresSelection: true,
        currentMode: null,
        hydrating: false,
        hydrated: true,
      });
      return;
    }

    set({
      activeBusinessId: null,
      activeBusinessName: null,
      memberships: [],
      requiresSelection: false,
      currentMode: serverContext?.current_mode ?? null,
      hydrating: false,
      hydrated: true,
    });
  },

  setActiveBusiness: async (businessId) => {
    const sessionToken = getOrCreateSessionToken();
    const result = await callMoneyRpc<{ business_id: string; business_name: string; expires_at: string }>(
      'set_active_business',
      { p_business_id: businessId, p_session_token: sessionToken },
    );

    if (!result.success) {
      return { success: false, error: result.error } as MoneyRpcResult<{
        businessId: string;
        businessName: string;
      }>;
    }

    set({
      activeBusinessId: result.data.business_id,
      activeBusinessName: result.data.business_name,
      requiresSelection: false,
      currentMode: 'business',
    });
    window.dispatchEvent(new CustomEvent(ACTIVE_BUSINESS_CHANGED_EVENT));

    return {
      success: true,
      alreadyExists: result.alreadyExists,
      data: { businessId: result.data.business_id, businessName: result.data.business_name },
    };
  },

  reset: () => {
    set({
      activeBusinessId: null,
      activeBusinessName: null,
      memberships: [],
      requiresSelection: false,
      currentMode: null,
      hydrated: false,
      hydrating: false,
    });
  },
}));

/**
 * Hook contract per WIRING §3.10. Subscribes to the store and exposes the
 * canonical shape; mutation hooks can read raw state via getState().
 */
export function useActiveBusiness() {
  const activeBusinessId = useActiveBusinessStore((s) => s.activeBusinessId);
  const activeBusinessName = useActiveBusinessStore((s) => s.activeBusinessName);
  const requiresSelection = useActiveBusinessStore((s) => s.requiresSelection);
  const memberships = useActiveBusinessStore((s) => s.memberships);
  const currentMode = useActiveBusinessStore((s) => s.currentMode);
  const setActiveBusiness = useActiveBusinessStore((s) => s.setActiveBusiness);

  return {
    activeBusinessId,
    activeBusinessName,
    requiresSelection,
    memberships,
    currentMode,
    setActiveBusiness,
  };
}
